import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const CalendarView = ({ shifts, selectedShifts, onShiftSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get current month and year
  const currentMonth = currentDate?.getMonth();
  const currentYear = currentDate?.getFullYear();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth?.getDate();
  const startingDayOfWeek = firstDayOfMonth?.getDay();

  // Generate calendar days
  const calendarDays = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays?.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays?.push(day);
  }

  // Get shifts for a specific day
  const getShiftsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentYear}-${String(currentMonth + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
    return shifts?.filter(shift => shift?.date === dateStr);
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentMonth + direction);
    setCurrentDate(newDate);
  };

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  const getShiftStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success text-success-foreground';
      case 'pending':
        return 'bg-warning text-warning-foreground';
      case 'cancelled':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">
          {monthNames?.[currentMonth]} {currentYear}
        </h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            iconSize={16}
            onClick={() => navigateMonth(-1)}
          />
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            iconSize={16}
            onClick={() => navigateMonth(1)}
          />
        </div>
      </div>
      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {dayNames?.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {calendarDays?.map((day, index) => {
            const dayShifts = getShiftsForDay(day);
            const isToday = day && 
              new Date()?.getDate() === day && 
              new Date()?.getMonth() === currentMonth && 
              new Date()?.getFullYear() === currentYear;

            return (
              <div
                key={index}
                className={`min-h-24 p-2 border border-border rounded-lg ${
                  day ? 'bg-background hover:bg-muted/50 cursor-pointer' : 'bg-muted/20'
                } ${isToday ? 'ring-2 ring-primary' : ''}`}
              >
                {day && (
                  <>
                    <div className="text-sm font-medium text-foreground mb-1">
                      {day}
                    </div>
                    <div className="space-y-1">
                      {dayShifts?.slice(0, 2)?.map((shift) => (
                        <div
                          key={shift?.id}
                          className={`text-xs px-2 py-1 rounded-full truncate ${getShiftStatusColor(shift?.status)}`}
                          title={`${shift?.title} (${shift?.startTime} - ${shift?.endTime})`}
                        >
                          {shift?.title}
                        </div>
                      ))}
                      {dayShifts?.length > 2 && (
                        <div className="text-xs text-muted-foreground px-2">
                          +{dayShifts?.length - 2} weitere
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Calendar Legend */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-sm text-muted-foreground">Bestätigt</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <span className="text-sm text-muted-foreground">Ausstehend</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-error rounded-full"></div>
            <span className="text-sm text-muted-foreground">Storniert</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;